import { InputAdornment } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { fixedChargesEditColumnsSelectors, getSelectorAttribute } from '@weft/app-selectors';
import type { FixedProductInput } from '@weft/types';
import { DEFAULT_CURRENCY, PRICING_MODELS } from '@weft/types';
import { isNullOrUndefined, raiseUnreachable } from '@weft/utils';
import type { FieldPath } from 'react-hook-form';
import { getCurrencySymbol } from '../../../../../../services/currency-symbol-mapper';

import { FixedChargeAddonsCell } from '../fixed-charges-edit-table-components/FixedChargeAddonsCell';
import { ProductEditCell } from '../fixed-charges-edit-table-components/ProductEditCell';
import { RemoveFixedProductRow } from '../fixed-charges-edit-table-components/RemoveFixedProductRow';

export const FIXED_CHARGES_EDIT_COLUMNS_KEYS = Object.freeze({
  TYPE: 'variants.0.pricing.pricingModel',
  NAME: 'variants.0.name',
  DESCRIPTION: 'variants.0.description',
  OTHER: 'other',
  QUANTITY: 'quantity',
} satisfies Record<string, FieldPath<FixedProductInput> | 'other' | 'quantity'>);

export const FIXED_CHARGES_EDIT_COLUMNS: GridColDef<FixedProductInput>[] = [
  {
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.TYPE,
    headerName: 'Type',
    sortable: false,
    minWidth: 180,
    valueGetter: (params) => getFixedProductLabel(params.row.variants[0].pricing.pricingModel),
  },
  {
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.NAME,
    headerName: 'Service name',
    flex: 2,
    sortable: false,
    renderCell: ({ row, field }) => (
      <ProductEditCell
        {...getSelectorAttribute(fixedChargesEditColumnsSelectors.nameInput)}
        fixedProduct={row}
        field={field as FieldPath<FixedProductInput>}
        placeholder="Service name"
        tooltipPlacement="top-start"
        fullWidth
      />
    ),
  },
  {
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.QUANTITY,
    headerName: 'Quantity',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const fieldPath = getFixedProductQuantityFieldPath(row.variants[0].pricing.pricingModel);

      if (isNullOrUndefined(fieldPath)) {
        return <span>-</span>;
      }

      return (
        <ProductEditCell
          fixedProduct={row}
          field={fieldPath}
          valueAsNumber
          type="number"
          placeholder="0"
          tooltipPlacement="top-start"
          fullWidth
        />
      );
    },
  },
  {
    field: 'amount',
    headerName: 'Price',
    flex: 2,
    sortable: false,
    renderCell: ({ row }) => {
      const isCommission = row.variants[0].pricing.pricingModel === PRICING_MODELS.Commission;

      const fieldPath: FieldPath<FixedProductInput> = isCommission ? 'variants.0.pricing.priceConfiguration.percentage' : 'variants.0.pricing.priceConfiguration.price';
      const prefix = isCommission ? '%' : getCurrencySymbol(DEFAULT_CURRENCY);

      return (
        <ProductEditCell
          {...getSelectorAttribute(fixedChargesEditColumnsSelectors.amountInput)}
          fixedProduct={row}
          field={fieldPath}
          valueAsNumber
          type="number"
          placeholder="0"
          tooltipPlacement="top-start"
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  paddingBottom: '2px',
                }}
              >
                {prefix}
              </InputAdornment>
            ),
          }}
        />
      );
    },
  },
  {
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.OTHER,
    headerName: 'Add on',
    headerClassName: 'pr-4',
    headerAlign: 'right',
    cellClassName: 'justify-end items-center',
    sortable: false,
    flex: 2,
    renderCell: ({ row }) => <FixedChargeAddonsCell fixedProduct={row}/>,
  },
  {
    field: 'deleteRow',
    headerName: '',
    cellClassName: 'justify-center',
    width: 30,
    renderCell: ({ row }) => (
      <RemoveFixedProductRow
        {...getSelectorAttribute(fixedChargesEditColumnsSelectors.removeRowButton)}
        fixedProduct={row}
      />
    ),
  },
];

function getFixedProductLabel(fixedProductType:
                                typeof PRICING_MODELS.FixedOneTime |
                                typeof PRICING_MODELS.FixedPerUnit |
                                typeof PRICING_MODELS.Subscription |
                                typeof PRICING_MODELS.CommercialTerms |
                                typeof PRICING_MODELS.Commission |
                                typeof PRICING_MODELS.Installments |
                                typeof PRICING_MODELS.Refund): string {
  switch (fixedProductType) {
    case PRICING_MODELS.FixedOneTime:
      return 'One-Time Fee';
    case PRICING_MODELS.FixedPerUnit:
      return 'Fixed Per Unit';
    case PRICING_MODELS.Subscription:
      return 'Subscription';
    case PRICING_MODELS.CommercialTerms:
      return 'Yearly Terms';
    case PRICING_MODELS.Commission:
      return 'Commission';
    case PRICING_MODELS.Installments:
      return 'Installments';
    case PRICING_MODELS.Refund:
      return 'Refund';
    default:
      return raiseUnreachable(fixedProductType);
  }
}

function getFixedProductQuantityFieldPath(fixedProductType:
                                            typeof PRICING_MODELS.FixedOneTime |
                                            typeof PRICING_MODELS.FixedPerUnit |
                                            typeof PRICING_MODELS.Subscription |
                                            typeof PRICING_MODELS.CommercialTerms |
                                            typeof PRICING_MODELS.Commission |
                                            typeof PRICING_MODELS.Installments |
                                            typeof PRICING_MODELS.Refund): FieldPath<FixedProductInput> | undefined {
  switch (fixedProductType) {
    case PRICING_MODELS.Installments:
      return 'variants.0.pricing.priceConfiguration.installments';
    case PRICING_MODELS.FixedPerUnit:
      return 'variants.0.pricing.priceConfiguration.units';
    case PRICING_MODELS.Subscription:
    case PRICING_MODELS.CommercialTerms:
    case PRICING_MODELS.Commission:
    case PRICING_MODELS.FixedOneTime:
    case PRICING_MODELS.Refund:
      return undefined;
    default:
      return raiseUnreachable(fixedProductType);
  }
}